/**
 * CSV Import Parser
 * Supports importing annual financial statement data from CSV files.
 * Expected format: columns include fiscalYear + key financial fields.
 */

import type {
  AnnualIncomeStatement,
  AnnualBalanceSheet,
  AnnualCashFlow,
  CsvImportData,
} from '@/types/stock';

function parseRow(row: Record<string, string>): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(row)) {
    const num = parseFloat(value.replace(/[$,\s]/g, ''));
    result[key.trim().toLowerCase().replace(/\s+/g, '_')] = isNaN(num) ? 0 : num;
  }
  return result;
}

function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = values[i] ?? '';
    });
    return row;
  });
}

// ============================================================
// Income Statement CSV
// Expected columns (case-insensitive):
// fiscal_year, revenue, gross_profit, operating_income, net_income, eps, shares_outstanding
// ============================================================

export function parseIncomeStatementCsv(text: string): AnnualIncomeStatement[] {
  const rows = parseCsv(text);
  return rows.map((row) => {
    const r = parseRow(row);
    return {
      fiscalYear: Math.round(r['fiscal_year'] || r['year'] || r['fiscalyear'] || 0),
      revenue: r['revenue'] || r['total_revenue'] || r['totalrevenue'] || 0,
      grossProfit: r['gross_profit'] || r['grossprofit'] || 0,
      operatingIncome: r['operating_income'] || r['operatingincome'] || r['ebit'] || 0,
      netIncome: r['net_income'] || r['netincome'] || 0,
      eps: r['eps'] || r['earnings_per_share'] || r['earningspershare'] || 0,
      sharesOutstanding: r['shares_outstanding'] || r['sharesoutstanding'] || 1,
    };
  }).filter((r) => r.fiscalYear > 1900 && r.revenue > 0)
    .sort((a, b) => a.fiscalYear - b.fiscalYear);
}

// ============================================================
// Balance Sheet CSV
// Expected columns:
// fiscal_year, current_assets, current_liabilities, total_assets,
// total_liabilities, total_equity, long_term_debt, cash
// ============================================================

export function parseBalanceSheetCsv(text: string): AnnualBalanceSheet[] {
  const rows = parseCsv(text);
  return rows.map((row) => {
    const r = parseRow(row);
    return {
      fiscalYear: Math.round(r['fiscal_year'] || r['year'] || r['fiscalyear'] || 0),
      currentAssets: r['current_assets'] || r['currentassets'] || 0,
      currentLiabilities: r['current_liabilities'] || r['currentliabilities'] || 0,
      totalAssets: r['total_assets'] || r['totalassets'] || 0,
      totalLiabilities: r['total_liabilities'] || r['totalliabilities'] || 0,
      totalEquity: r['total_equity'] || r['totalequity'] || r['shareholders_equity'] || 0,
      longTermDebt: r['long_term_debt'] || r['longtermdebt'] || 0,
      cash: r['cash'] || r['cash_and_equivalents'] || r['cashandequivalents'] || 0,
    };
  }).filter((r) => r.fiscalYear > 1900)
    .sort((a, b) => a.fiscalYear - b.fiscalYear);
}

// ============================================================
// Cash Flow CSV
// Expected columns:
// fiscal_year, operating_cash_flow, capital_expenditures, free_cash_flow, dividends_paid
// ============================================================

export function parseCashFlowCsv(text: string): AnnualCashFlow[] {
  const rows = parseCsv(text);
  return rows.map((row) => {
    const r = parseRow(row);
    const operatingCashFlow = r['operating_cash_flow'] || r['operatingcashflow'] || 0;
    const capex = r['capital_expenditures'] || r['capitalexpenditures'] || r['capex'] || 0;
    return {
      fiscalYear: Math.round(r['fiscal_year'] || r['year'] || r['fiscalyear'] || 0),
      operatingCashFlow,
      capitalExpenditures: capex,
      freeCashFlow: r['free_cash_flow'] || r['freecashflow'] || (operatingCashFlow - Math.abs(capex)),
      dividendsPaid: r['dividends_paid'] || r['dividendspaid'] || 0,
    };
  }).filter((r) => r.fiscalYear > 1900)
    .sort((a, b) => a.fiscalYear - b.fiscalYear);
}

// ============================================================
// CSV template generators (for download)
// ============================================================

export function generateIncomeStatementTemplate(): string {
  const header = 'fiscal_year,revenue,gross_profit,operating_income,net_income,eps,shares_outstanding';
  const example = '2023,50000000000,20000000000,10000000000,8000000000,5.25,1524000000';
  return `${header}\n${example}\n`;
}

export function generateBalanceSheetTemplate(): string {
  const header = 'fiscal_year,current_assets,current_liabilities,total_assets,total_liabilities,total_equity,long_term_debt,cash';
  const example = '2023,135000000000,125000000000,352000000000,287000000000,65000000000,111000000000,29700000000';
  return `${header}\n${example}\n`;
}

export function generateCashFlowTemplate(): string {
  const header = 'fiscal_year,operating_cash_flow,capital_expenditures,free_cash_flow,dividends_paid';
  const example = '2023,110000000000,11000000000,99000000000,15000000000';
  return `${header}\n${example}\n`;
}
